/**
 * Shared trail UI used by both TrailReview (standalone page) and
 * LearningMode (in-session overlay).
 *
 * Extracted so the two layouts share the same rendering, marking and
 * case-field logic rather than forking it.
 *
 * Exports:
 *   StepList        — steps grouped under TURN N headings
 *   RuledOutNote    — the optional one-line "why" under a Ruled out mark,
 *                     shared with the graph's detail panel
 *   OutcomeSelector — the "How did it end?" radio group
 *   CaseFields      — symptom / possible causes / resolution + the composed
 *                     read-only SESSION SUMMARY (which folds in a DIAGNOSTICS
 *                     block derived from the steps) + what we learned
 *   saveCaseFields  — PATCH helper that degrades when the backend does not
 *                     yet accept the extended fields
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Check, X, HelpCircle, ChevronUp, ChevronDown, ChevronRight, Trash2, Copy, Plus,
  CircleCheck, CircleSlash, ArrowUpRight, LogOut,
} from "lucide-react";
import "./styles/trail-steps.css";

/* ── constants (exported for consumers that need them) ──────────────── */

export const KIND_LABEL = {
  decision: "Engine proposed",
  digression: "You went your own way",
  traversed: "Check run",
  resolution: "Conclusion",
};

/**
 * `role` (entry | symptom | cause | fix | decision | check) is derived by the
 * backend from the node id — see server/app/learning/review.py:node_role — and
 * says what the step IS. It is a better card label than `kind`, which only says
 * where the row came from. Absent on a response from an older backend, in which
 * case the card falls back to KIND_LABEL exactly as before.
 */
export const ROLE_LABEL = {
  entry: "Entry",
  symptom: "Symptom",
  cause: "Cause",
  fix: "Fix",
  decision: "Decision",
  check: "Check",
};

/**
 * The type label every narration line carries. Deliberately ONE constant and
 * not `ROLE_LABEL[step.role]`: narration rows are `entry` / `symptom` to the
 * backend, and the decision is that the reviewer reads them as one set. No
 * per-row fallback — a row whose role changes must not change this label.
 */
const NARRATION_LABEL = "Check";

/**
 * Does this step ask a question a human can answer?
 *
 * The test is `markable !== false`, not `markable === true`, everywhere: a
 * response from a backend that does not send the field at all must behave
 * exactly as it does today (every step markable). Mirrors the local unmarked
 * counts in TrailReview / LearningMode and the server's submit gate.
 */
export function isMarkable(step) {
  return !!step && step.markable !== false;
}

/** The label on a step: role first, then kind. An engineer_jump keeps its own
    label — that a human overrode the engine outranks what node they landed on,
    and the amber card styling is keyed to the same fact. */
export function stepLabel(step) {
  if (!step) return "";
  if (step.type === "digression") return KIND_LABEL.digression;
  return ROLE_LABEL[step.role] || KIND_LABEL[step.type] || step.type || "Step";
}

/* `note` is a consequence, shown as a small muted line under the hint rather
   than folded into it. `Icon` + `tone` drive the card's tint (see
   trail-steps.css, "outcome cards"). */
export const OUTCOMES = [
  { value: "resolved", label: "Resolved", hint: "The fix was confirmed to work", Icon: CircleCheck, tone: "resolved" },
  { value: "dead_ended", label: "Dead-ended", hint: "Closed with no actionable finding", Icon: CircleSlash, tone: "dead" },
  { value: "escalated", label: "Escalated", hint: "Handed off — the finding was not enough", Icon: ArrowUpRight, tone: "escalated" },
  { value: "abandoned", label: "Abandoned", hint: "Left mid-diagnosis", note: "Excluded from learning", Icon: LogOut, tone: "abandoned" },
];

export const RESULTS = [
  { value: "worked", label: "Worked", Icon: Check, cls: "tr-mark--worked" },
  { value: "ruled_out", label: "Ruled out", Icon: X, cls: "tr-mark--ruled" },
  { value: "inconclusive", label: "Can't tell", Icon: HelpCircle, cls: "tr-mark--inconclusive" },
];

/* ── helpers ────────────────────────────────────────────────────────── */

export function stepHeadline(step) {
  // Prefer human-readable text; fall back through edge label, a derived
  // phrase from kind + node IDs, and finally a numbered placeholder so a
  // card is never blank.
  if (step.proposed_text) return step.proposed_text;
  if (step.query_text) return step.query_text;
  if (step.edge_label) return step.edge_label;
  // Derive a short phrase from kind + node IDs when all text fields are empty.
  const kindWord = KIND_LABEL[step.type] || step.type || "Step";
  if (step.from_nid && step.to_nid) return `${kindWord}: ${step.from_nid} → ${step.to_nid}`;
  if (step.to_nid) return `${kindWord}: ${step.to_nid}`;
  if (step.from_nid) return `${kindWord}: ${step.from_nid}`;
  return `Step ${step.step_no}`;
}

export function stepSecondary(step) {
  if (!step.from_nid && !step.to_nid) return null;
  // A jump step can have from_nid but null to_nid — showing "NODE -> null"
  // is a rendering bug. Show only what exists.
  if (step.from_nid && step.to_nid) return `${step.from_nid} → ${step.to_nid}`;
  return step.from_nid || step.to_nid;
}

/* ── step evidence: edge_label / siblings / sources_shown / meta ─────
   Every one of these is absent on trails captured before the backend
   started writing them, so the whole layer is opt-in per field: when a
   field is empty NOTHING renders — no label, no "0 sources", no chip. */

const asStr = (v) => (v == null ? "" : String(v)).trim();

/** Arrays arrive as TEXT[] (array), null, or — after a bad round-trip — junk. */
const asArray = (v) => (Array.isArray(v) ? v.filter((x) => x != null && x !== "") : []);

const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/** `meta` is JSONB: an object over JSON, a string over some clients, or absent. */
export function parseMeta(raw) {
  if (isPlainObject(raw)) return raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      return isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

const sameText = (a, b) => asStr(a).toLowerCase() === asStr(b).toLowerCase();

/* ── source titles ───────────────────────────────────────────────────
   Retrieval hands us whatever the ingest pipeline stored as a title, and for
   a large share of the corpus that is the raw object key:
   "379978083_5e2a2efdaade46da85d97f3087b0e11e-210626-1744-2422". Printing a
   50-character id where a document name belongs is what makes the evidence
   block unreadable, so a title is cleaned before it is shown and, when there
   is nothing human left in it, replaced by a numbered label plus a short
   quiet reference. */

/** Runs that carry no meaning to a reader: long hex blobs and long digit runs. */
const HEXY_RE = /^[0-9a-f]{12,}$/i;
const DIGITY_RE = /^\d{4,}$/;
const KNOWN_EXT_RE = /\.(pdf|docx?|pptx?|xlsx?|txt|md|html?|csv|json|eml|msg)$/i;
const MAX_TITLE = 64;

const truncateTitle = (s) => (s.length > MAX_TITLE ? `${s.slice(0, MAX_TITLE - 1).trimEnd()}…` : s);

/**
 * A readable label for one source, plus (only when the original was pure id)
 * a short reference so the raw value is still traceable.
 * Returns { label, ref? } — `ref` is left undefined when unused so the shape
 * of a normal source is unchanged.
 */
export function prettySourceTitle(raw, i = 0) {
  const numbered = { label: `Source ${i + 1}` };
  // Anything that is not text has no name in it — never String() an object.
  if (typeof raw !== "string" && typeof raw !== "number") return numbered;
  const s = asStr(raw);
  if (!s) return numbered;
  // A path or object key: only the last segment can carry a name.
  const base = s.split(/[\\/]/).pop().replace(KNOWN_EXT_RE, "").trim() || s;
  const unreadable = () =>
    (base.length > 8 ? { label: numbered.label, ref: `${base.slice(0, 8)}…` } : numbered);

  if (!/[a-z]/i.test(base)) return unreadable();

  const hashy = /[0-9a-f]{12,}/i.test(base) || base.length > MAX_TITLE;
  if (!hashy) return { label: truncateTitle(base) };

  // Drop the machine-generated runs and see whether a name survives.
  const words = base.split(/[\s_\-.]+/).filter((w) => w && !HEXY_RE.test(w) && !DIGITY_RE.test(w));
  const name = words.join(" ").trim();
  if (name.length >= 3 && /[a-z]/i.test(name)) return { label: truncateTitle(name) };

  return unreadable();
}

/** A meta.sources entry — object, bare string, or something unexpected. */
function normalizeSource(raw, i) {
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    const { label, ref } = prettySourceTitle(t, i);
    return { title: label, ref, score: null, cited: false, why: "" };
  }
  if (!isPlainObject(raw)) return null;
  const given = asStr(raw.title) || asStr(raw.id) || asStr(raw.source_id);
  const { label, ref } = given ? prettySourceTitle(given, i) : { label: `Source ${i + 1}` };
  const n = Number(raw.score);
  return {
    title: label,
    ref,
    score: Number.isFinite(n) ? (Math.abs(n) < 10 ? n.toFixed(2) : String(Math.round(n))) : null,
    cited: raw.cited === true,
    why: asStr(raw.why_selected),
  };
}

/**
 * Everything the step UI needs from the four new columns, already made safe.
 *
 * `branch` is suppressed when stepHeadline() already fell back to edge_label,
 * so the chip never echoes the line right above it.
 * Returns `{ hasAny: false }` shaped data for every pre-existing step.
 */
export function stepEvidence(step) {
  const blank = {
    branch: "", siblings: [], sources: [], answer: "",
    cacheHit: false, hasDetail: false, hasAny: false, summary: "",
  };
  if (!step) return blank;

  const meta = parseMeta(step.meta);
  const labels = isPlainObject(meta.sibling_labels) ? meta.sibling_labels : {};

  const edge = asStr(step.edge_label);
  const branch = edge && !sameText(edge, stepHeadline(step)) ? edge : "";

  // A sibling with no entry in sibling_labels falls back to its bare nid.
  const siblings = asArray(step.siblings).map((nid) => {
    const key = String(nid);
    const label = asStr(labels[key]);
    return { nid: key, label: label || key, labelled: !!label };
  });

  const fromMeta = asArray(meta.sources).map(normalizeSource).filter(Boolean);
  const sources = fromMeta.length
    ? fromMeta
    : asArray(step.sources_shown).map((id, i) => {
      const { label, ref } = prettySourceTitle(String(id), i);
      return { title: label, ref, score: null, cited: false, why: "" };
    });

  const rawAnswer = asStr(meta.answer);
  const answer =
    rawAnswer && !sameText(rawAnswer, edge) && !sameText(rawAnswer, stepHeadline(step))
      ? rawAnswer
      : "";

  const parts = [];
  if (siblings.length) {
    parts.push(`${siblings.length} other branch${siblings.length > 1 ? "es" : ""}`);
  }
  if (sources.length) {
    parts.push(`${sources.length} source${sources.length > 1 ? "s" : ""}`);
  }
  const hasDetail = parts.length > 0 || !!answer;

  return {
    branch,
    siblings,
    sources,
    answer,
    cacheHit: meta.cache_hit === true,
    hasDetail,
    hasAny: hasDetail || !!branch,
    summary: parts.length ? parts.join(" · ") : (answer ? "What was answered" : ""),
  };
}

/** The always-visible chip: the branch condition actually taken. */
export function EvidenceBranch({ evidence }) {
  if (!evidence?.branch) return null;
  return (
    <p className="tr-ev-branch" title={evidence.branch}>
      <span className="tr-ev-arrow" aria-hidden="true">→</span>
      <span className="tr-ev-branch-text">{evidence.branch}</span>
    </p>
  );
}

/**
 * The branches NOT taken, rendered as a pair with the branch that WAS —
 * one ✗ line each, directly under the → line, never behind a disclosure.
 *
 * A decision is only readable as a pair: "we went here, we did not go there".
 * Hiding the rejected half one click away meant the reviewer had to expand
 * every card to see what the engine passed over. The sibling's node id stays
 * inline and quiet, exactly as it is in the folded body.
 *
 * Used by the CARD only. The graph's detail panel has the room to show the
 * whole evidence block open, so it keeps reading the siblings out of
 * EvidenceBody — which is why that block is still there.
 */
export function EvidenceNotTaken({ evidence }) {
  const siblings = evidence?.siblings || [];
  if (!siblings.length) return null;
  return (
    <ul className="tr-ev-list tr-not-taken">
      {siblings.map((s) => (
        <li
          key={s.nid}
          className={`tr-ev-sib tr-not${s.labelled ? "" : " tr-ev-sib--raw"}`}
        >
          {/* lucide X, same icon family as the "Ruled out" mark — a literal
              character would not sit on the same optical grid as the → above. */}
          <X
            size={11}
            strokeWidth={2.75}
            className="tr-not-icon"
            role="img"
            aria-label="Not taken"
          />
          <span className="tr-ev-sib-text">{s.label}</span>
          {s.labelled && <span className="tr-ev-nid">{s.nid}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * One retrieved source, in the card treatment the engineer already reads in
 * chat (dashboard5/SourceCard.jsx). Same classes, so the two cannot drift:
 * .d5-source-card / -top / -index / .d5-source-title / .d5-source-card-badges
 * / .d5-source-tick (the green cited tick) / .d5-score-badge / .d5-source-why
 * / .d5-source-meta. trail-steps.css only compacts them for a narrow pane.
 *
 * It is NOT SourceCard itself: that component takes a chat source (citation,
 * url, source_type, causal, flag/open-map handlers) and its whole body is a
 * link out to a document. A step's source carries a title, a score, a cited
 * flag and a why — no URL — so there is nothing to open and a link
 * affordance would be a lie.
 */
function SourceEvidenceCard({ source, index }) {
  const { title, ref, score, cited, why } = source;
  return (
    <div
      className={`d5-source-card tr-src-card${cited ? " d5-source-card-cited" : ""}`}
      title={why || title}
    >
      <div className="d5-source-card-top">
        <span className="d5-source-index">{index + 1}</span>
        <span className="d5-source-title">{title}</span>
        {/* Only set when prettySourceTitle() had to replace a pure-id name.
            It rides WITH the title — as its own footer row it read as a
            dangling hash under the card and carried no visible relationship
            to the "Source N" label it is the reference for. */}
        {ref && (
          <span className="tr-ev-nid tr-src-ref" title={`Source id ${ref}`}>
            {ref}
          </span>
        )}
        <span className="d5-source-card-badges">
          {cited && (
            <span
              className="d5-source-tick tr-ev-cited"
              role="img"
              aria-label="Cited in the answer"
            >
              <Check size={9} strokeWidth={3.5} />
            </span>
          )}
          {/* A bare 0.77 means nothing on its own — say what it measures. */}
          {score != null && (
            <span className="d5-score-badge tr-ev-score">
              <span className="tr-ev-score-cap">match</span>
              <span className="tr-ev-score-val">{score}</span>
            </span>
          )}
        </span>
      </div>

      {why && (
        <div className="d5-source-why">
          <span className="d5-source-why-label">Why selected</span>
          <span className="d5-source-why-text">{why}</span>
        </div>
      )}
    </div>
  );
}

/** The expanded detail — shared by the card's disclosure and the graph panel. */
export function EvidenceBody({ evidence }) {
  if (!evidence?.hasDetail) return null;
  const { siblings, sources, answer, cacheHit } = evidence;
  /* `hasDetail` is computed BEFORE the card folds the siblings out (they are
     already drawn as ✗ lines above it), so a step whose only evidence is a
     sibling reached this far and rendered the panel chrome around nothing —
     an empty grey box. Harmless while the box was a narrow strip; at full
     card width it is just a hole. Gate on what will actually be drawn.
     The graph's detail panel passes UNFOLDED evidence, so it is untouched. */
  if (!siblings.length && !answer && !sources.length && !cacheHit) return null;
  return (
    <div className="tr-ev-body">
      {siblings.length > 0 && (
        <div className="tr-ev-block">
          <span className="tr-ev-label">Not taken</span>
          <ul className="tr-ev-list">
            {/* The nid sits INLINE, small and mono — as a `title` it became a
                black tooltip floating over the graph. */}
            {siblings.map((s) => (
              <li
                key={s.nid}
                className={`tr-ev-sib${s.labelled ? "" : " tr-ev-sib--raw"}`}
              >
                <span className="tr-ev-sib-text">{s.label}</span>
                {s.labelled && <span className="tr-ev-nid">{s.nid}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {answer && (
        <div className="tr-ev-block">
          <span className="tr-ev-label">Answered</span>
          <p className="tr-ev-answer">{answer}</p>
        </div>
      )}

      {sources.length > 0 && (
        <div className="tr-ev-block">
          <span className="tr-ev-label">Sources</span>
          {/* The same wrapping grid the chat uses, compacted by
              .tr-src-cards so it degrades to one column in a narrow pane. */}
          <div className="d5-source-cards-row tr-src-cards">
            {sources.map((s, i) => (
              <SourceEvidenceCard key={`${s.title}-${i}`} source={s} index={i} />
            ))}
          </div>
        </div>
      )}

      {cacheHit && <span className="tr-ev-tag">Answered from cache</span>}
    </div>
  );
}

/**
 * One quiet summary line ("2 other branches · 3 sources") that opens the
 * body in place. Renders nothing when there is nothing behind it, so a
 * legacy step's card keeps exactly the height it has today.
 */
export function EvidenceDisclosure({ evidence }) {
  const [open, setOpen] = useState(false);
  if (!evidence?.hasDetail) return null;
  return (
    /* `tr-ev--open` is what lets the card stay ONE column: collapsed, the
       disclosure is a short inline line the mark control can share a row
       with; open, it has to take the full card width so the evidence is not
       squeezed into a centre strip with the marks stranded beside it. */
    <div
      className={`tr-ev${open ? " tr-ev--open" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="tr-ev-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        <span>{evidence.summary}</span>
      </button>
      {open && <EvidenceBody evidence={evidence} />}
    </div>
  );
}

/* ── the reason behind a verdict ────────────────────────────────────────
   The field is named `ruled_out_note` in meta.review and surfaces as
   `result_note` — a historical name from when only Ruled out carried a
   reason. The AI now writes a reason for every verdict (worked,
   inconclusive, ruled_out), so the gate is relaxed to show one for any
   verdict that has it. The storage key was not renamed because hundreds of
   existing rows already use it. */

/**
 * The stored one-line "why" for a step. Always a string.
 *
 * `result_note` is what the backend lifts out of `meta.review.ruled_out_note`
 * (server/app/learning/review.py:annotate) so the UI never has to know the
 * storage shape. The meta fallback is for a response that predates that lift —
 * and for the graph, which is handed raw step rows. Junk in any of those
 * places reads as "no note", never as a crash.
 */
export function stepResultNote(step) {
  if (!step) return "";
  if (typeof step.result_note === "string") return step.result_note;
  if (step.result_note != null && typeof step.result_note !== "object") {
    return String(step.result_note);
  }
  const review = parseMeta(step.meta).review;
  const note = isPlainObject(review) ? review.ruled_out_note : null;
  if (typeof note === "string") return note;
  return note == null || typeof note === "object" ? "" : String(note);
}

/**
 * The optional one-line reason under a verdict.
 *
 * ALWAYS MOUNTED, decides internally whether to draw anything. That is the
 * whole trick: the typed text lives in this component's own state, so if the
 * parent only mounted it while a specific mark was set then toggling the mark
 * away and back would unmount it and throw the sentence away before it was
 * ever saved. Mounted unconditionally, an accidental double-click costs nothing.
 *
 * Renders for ANY verdict that is set — not just "ruled_out". The AI now writes
 * a reason for worked / inconclusive / ruled_out, and this component shows it.
 *
 * Saves on BLUR and on Enter, never per keystroke: the two screens debounce
 * marks into one PATCH, and a note is not worth a request per character.
 *
 * Props:
 *   stepNo     — step_no, the key the PATCH is addressed by
 *   marked     — the current mark for this step (any truthy value to render)
 *   storedNote — the note as the screen currently knows it (live map first,
 *                stored value second)
 *   readOnly   — render the note as quiet text; never an input
 *   onNote     — (stepNo, value) => void, fired on blur / Enter
 */
export function RuledOutNote({ stepNo, marked, storedNote = "", readOnly = false, onNote }) {
  const [value, setValue] = useState(storedNote || "");
  /* What is on screen, readable from the effect without making `value` a
     dependency — the effect must react to the PROP changing, not to typing. */
  const valueRef = useRef(value);
  /* "The user has typed something we have not seen come back yet." A refetch
     re-seeds the note only while this is false, so a background refresh can
     never overwrite a sentence mid-word. It clears itself the moment the
     stored value catches up with what is on screen. */
  const dirtyRef = useRef(false);

  useEffect(() => {
    const next = storedNote || "";
    if (next === valueRef.current) {
      dirtyRef.current = false;
      return;
    }
    if (dirtyRef.current) return;
    valueRef.current = next;
    setValue(next);
  }, [storedNote]);

  const commit = useCallback(() => {
    onNote?.(stepNo, valueRef.current);
  }, [onNote, stepNo]);

  /* The field is a textarea that grows with its text: an AI-written reason is
     a sentence or two, and a one-line <input> showed "Path MTU Discovery
     enablement check was e…" with the rest scrolled out of sight. Height is
     reset then set to the content; the CSS caps it at four lines (it scrolls
     past that) and floors it at two. */
  const fieldRef = useRef(null);
  useLayoutEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  }, [value, marked, readOnly]);

  // Show for ANY verdict, not just ruled_out — the AI writes reasons for all.
  if (!marked) return null;

  if (readOnly) {
    const text = (storedNote || "").trim();
    if (!text) return null;
    return (
      <div className="tr-ro-note-wrap" onClick={(e) => e.stopPropagation()}>
        <p className="tr-ro-note">{text}</p>
      </div>
    );
  }

  return (
    /* The card itself is clickable (it scrolls the transcript to the turn), so
       every event inside the field stops here — same technique as
       EvidenceDisclosure. Typing an "e" must not re-scroll the pane. */
    <div
      className="tr-ro-note-wrap"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <textarea
        ref={fieldRef}
        rows={2}
        className="tr-ro-note-input"
        value={value}
        maxLength={500}
        placeholder="Reason (optional)"
        aria-label="Reason for this verdict (optional)"
        onChange={(e) => {
          dirtyRef.current = true;
          valueRef.current = e.target.value;
          setValue(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          // A note is one sentence, not a document: Enter still saves, as it
          // did when this was an <input>. The text WRAPS; it never breaks.
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
      />
    </div>
  );
}

/**
 * Group steps into turn buckets, keeping the flat index of every step so
 * reorder (which is index-based) keeps working across groups.
 *
 * Steps whose turn_no is null/undefined/non-numeric are NOT dropped — they
 * land in a trailing group with turnNo === null, rendered under an explicit
 * "Not tied to a turn" heading.
 *
 * Returns [{ turnNo: number|null, items: [{ step, flatIndex }] }].
 */
export function groupStepsByTurn(steps = []) {
  const groups = [];
  const byTurn = new Map();
  const untied = [];

  steps.forEach((step, flatIndex) => {
    if (!step) return;
    const raw = step.turn_no;
    const tn = raw == null || raw === "" || Number.isNaN(Number(raw)) ? null : Number(raw);
    const entry = { step, flatIndex };
    if (tn === null) {
      untied.push(entry);
      return;
    }
    let g = byTurn.get(tn);
    if (!g) {
      g = { turnNo: tn, items: [] };
      byTurn.set(tn, g);
      groups.push(g);
    }
    g.items.push(entry);
  });

  groups.sort((a, b) => a.turnNo - b.turnNo);
  if (untied.length) groups.push({ turnNo: null, items: untied });
  return groups;
}

/* ── StepList ───────────────────────────────────────────────────────── */

/**
 * The <ol> of step cards for ONE bucket of steps.
 *
 * Factored out of StepList so the merged turn timeline (TurnBreakdown) can
 * render a single turn's steps with the SAME cards, marks, notes and
 * `data-unmarked` attributes rather than growing a second implementation of
 * them. `items` carries the flat index of every step so reorder — which is
 * index-based over the WHOLE trail — keeps working inside a bucket.
 *
 * Props mirror StepList's, minus the grouping.
 */
export function StepItems({
  items = [],
  total = 0,
  results = {},
  notes = {},
  readOnly = false,
  canEdit = false,
  confirmedMarks = {},
  onMark,
  onNote,
  onStepClick,
  onMoveStep,
  onRemoveStep,
}) {
  return (
    /* ONE card shell per step, markable or not. A narration step keeps its
       own <li> at the top level of the list rather than being folded into a
       shared surface with its neighbours — the list reads as a single
       sequence of steps, which is what it is. What a step asks of the reader
       is said inside the card (a mark control, or nothing), never by its
       shell. */
    <ol className="tr-steps">
      {items.map(({ step, flatIndex }) => {
        const common = {
          step,
          flatIndex,
          total,
          marked: results[step.step_no],
          isInferred: step.result_source === "inferred" && !confirmedMarks[step.step_no],
          canEdit,
          onStepClick,
          onMoveStep,
          onRemoveStep,
        };
        return isMarkable(step) ? (
          <StepCard
            key={step.step_no}
            {...common}
            /* The live map wins over the stored value: what the engineer typed
               one second ago is the truth, even before the debounced PATCH has
               confirmed it. */
            storedNote={notes?.[step.step_no] ?? stepResultNote(step)}
            readOnly={readOnly}
            onMark={onMark}
            onNote={onNote}
          />
        ) : (
          <NarrationCard
            key={step.step_no}
            {...common}
            storedNote={notes?.[step.step_no] ?? stepResultNote(step)}
            readOnly={readOnly}
            onMark={onMark}
            onNote={onNote}
          />
        );
      })}
    </ol>
  );
}

/** Reorder / delete, shared by the card and the narration line. Renders
    nothing unless the screen passes canEdit and the matching handler. */
function StepActions({ step, flatIndex, total, canEdit, onMoveStep, onRemoveStep }) {
  if (!canEdit || (!onMoveStep && !onRemoveStep)) return null;
  return (
    <span className="tr-step-actions">
      {onMoveStep && (
        <>
          <button
            type="button"
            title="Move up"
            disabled={flatIndex === 0}
            onClick={(e) => { e.stopPropagation(); onMoveStep(flatIndex, -1); }}
            className="tr-step-btn"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            title="Move down"
            disabled={flatIndex === total - 1}
            onClick={(e) => { e.stopPropagation(); onMoveStep(flatIndex, 1); }}
            className="tr-step-btn"
          >
            <ChevronDown size={14} />
          </button>
        </>
      )}
      {onRemoveStep && (
        <button
          type="button"
          title="Remove step"
          onClick={(e) => { e.stopPropagation(); onRemoveStep(step.step_no); }}
          className="tr-step-btn tr-step-btn--danger"
        >
          <Trash2 size={13} />
        </button>
      )}
    </span>
  );
}

/**
 * A step that asks nothing of the engineer — a workflow entry, a root node, a
 * symptom restated. The backend calls these narration (`markable === false`,
 * about half of every trail).
 *
 * It renders the SAME card shell as StepCard — same <li class="tr-step">,
 * same .tr-step-head / .tr-step-kind / .tr-step-nid--inline / .tr-step-headline
 * — because two rows that both say CHECK and both describe a step of the walk
 * read as two broken variants of one component when one is a white card and
 * the other a grey strip. The shell is presentation; it carries no promise
 * that the row can be judged.
 *
 * What is genuinely different is kept different, and only that:
 *   - no evidence disclosure (narration carries none),
 *   - no Worked / Ruled out / Can't tell control. There are no siblings and
 *     nothing was decided here, so those three words would mean nothing —
 *     an always-disabled control is worse than no control.
 *   - a mark STORED before the markable split shipped is still shown (hiding
 *     stored data would be a silent loss) in exactly the pill a markable card
 *     uses for a set mark, but read-only: no button, nothing to click.
 *
 * `data-unmarked` is deliberately NOT set: the footer hint scrolls to the
 * first step still asking for a mark, and this is not one of them.
 */
function NarrationCard({
  step, flatIndex, total, marked, isInferred, canEdit, readOnly, onMark, onNote, storedNote = "",
  onStepClick, onMoveStep, onRemoveStep,
}) {
  const nid = step.to_nid || step.from_nid || "";
  const res = marked ? RESULTS.find((r) => r.value === marked) : null;
  return (
    <li
      onClick={() => onStepClick?.(step.turn_no)}
      data-step-no={step.step_no}
      className={[
        "tr-step",
        "tr-nar",
        marked ? `tr-step--${marked}` : "",
        marked ? "tr-nar--marked" : "",
      ].filter(Boolean).join(" ")}
    >
      <div className="tr-step-head">
        {/* One constant label for every narration row, by product decision: the
            reader is told what these rows ARE as a set, not what the backend
            happened to type each one (they are role `entry` / `symptom`). This
            is presentation only — `step.role` is untouched in the data, and the
            markable cards still label themselves from it via stepLabel().
            `data-role="check"` so the label takes the same colour the CHECK
            cards take, from the one rule that owns role tinting. */}
        <span className="tr-step-kind tr-nar-kind" data-role="check">
          {NARRATION_LABEL}
        </span>
        {nid && (
          <span className="tr-step-nid tr-step-nid--inline tr-nar-nid">{nid}</span>
        )}
        <StepActions
          step={step}
          flatIndex={flatIndex}
          total={total}
          canEdit={canEdit}
          onMoveStep={onMoveStep}
          onRemoveStep={onRemoveStep}
        />
      </div>

      <p className="tr-step-headline tr-nar-text">{stepHeadline(step)}</p>

      {/* Mark control — available but NOT REQUIRED for the submit gate.
          The graph view always shows it; the list must match so the two views
          never disagree about whether a step can be marked. `data-unmarked`
          is deliberately NOT set: the counters and the submit gate still only
          count isMarkable() steps, so a narration step left unmarked does not
          block submission. */}
      {!readOnly && (
        <div className="tr-marks">
          {RESULTS.map(({ value, label, Icon, cls }) => {
            const active = marked === value;
            return (
              <button
                key={value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMark?.(step.step_no, value);
                }}
                className={`tr-mark ${active ? cls : ""}`}
                aria-pressed={active}
              >
                <Icon size={12} />
                <span>{label}</span>
              </button>
            );
          })}
          {marked && isInferred && (
            <span className="tr-ai-badge" title="Suggested by AI — click a mark to confirm">AI</span>
          )}
        </div>
      )}
      {readOnly && res && (
        <div className="tr-marks">
          <span className={`tr-mark-ro tr-nar-mark ${res.cls}`}>{res.label}</span>
          {marked && isInferred && (
            <span className="tr-ai-badge">AI</span>
          )}
        </div>
      )}
      <RuledOutNote
        stepNo={step.step_no}
        marked={marked}
        storedNote={storedNote}
        readOnly={readOnly}
        onNote={onNote}
      />
    </li>
  );
}

/** One markable step card — the steps that actually want a judgement. */
function StepCard({
  step,
  flatIndex,
  total,
  marked,
  isInferred,
  storedNote = "",
  readOnly,
  canEdit,
  onMark,
  onNote,
  onStepClick,
  onMoveStep,
  onRemoveStep,
}) {
  const evidence = useMemo(() => stepEvidence(step), [step]);
  /* The siblings are now shown in full above, so the disclosure must not list
     them a second time. Its SUMMARY still counts them — the line says what
     evidence the step carries, and the branch count is part of that. */
  const folded = useMemo(
    () => (evidence.siblings.length ? { ...evidence, siblings: [] } : evidence),
    [evidence]
  );
  const nid = stepSecondary(step);
  return (
    <li
      onClick={() => onStepClick?.(step.turn_no)}
      /* The footer hint scrolls to the first step still asking for a mark;
         these two attributes are how it finds one without a ref registry. */
      data-step-no={step.step_no}
      data-unmarked={marked ? undefined : "true"}
      className={[
        "tr-step",
        step.type === "digression" ? "tr-step--jump" : "",
        marked ? `tr-step--${marked}` : "",
      ].join(" ")}
    >
      {/* header row: what this step IS (role, not provenance) + the nid,
          which used to cost a line of its own. */}
      <div className="tr-step-head">
        <span className="tr-step-kind" data-role={step.role || ""}>
          {stepLabel(step)}
        </span>
        {nid && <span className="tr-step-nid tr-step-nid--inline">{nid}</span>}
        <StepActions
          step={step}
          flatIndex={flatIndex}
          total={total}
          canEdit={canEdit}
          onMoveStep={onMoveStep}
          onRemoveStep={onRemoveStep}
        />
      </div>

      {/* headline: the human-readable description */}
      <p className="tr-step-headline">{stepHeadline(step)}</p>

      {/* the decision, read as a pair: the branch taken, then each branch
          rejected directly beneath it */}
      <EvidenceBranch evidence={evidence} />
      <EvidenceNotTaken evidence={evidence} />

      {/* engineer query, if different from headline */}
      {step.query_text && step.proposed_text && (
        <p className="tr-step-query">"{step.query_text}"</p>
      )}

      {/* everything else folds behind one line */}
      <EvidenceDisclosure evidence={folded} />

      {/* compact segmented mark control */}
      {!readOnly && (
        <div className="tr-marks">
          {RESULTS.map(({ value, label, Icon, cls }) => {
            const active = marked === value;
            return (
              <button
                key={value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMark?.(step.step_no, value);
                }}
                className={`tr-mark ${active ? cls : ""}`}
                aria-pressed={active}
              >
                <Icon size={12} />
                <span>{label}</span>
              </button>
            );
          })}
          {marked && isInferred && (
            <span className="tr-ai-badge" title="Suggested by AI — click a mark to confirm">AI</span>
          )}
        </div>
      )}
      {readOnly && marked && (
        <div className="tr-marks">
          <span className={`tr-mark-ro ${RESULTS.find((r) => r.value === marked)?.cls || ""}`}>
            {RESULTS.find((r) => r.value === marked)?.label || marked}
          </span>
          {marked && isInferred && (
            <span className="tr-ai-badge">AI</span>
          )}
        </div>
      )}

      {/* Mounted for every card, in both branches, and self-gating on the mark
          — see RuledOutNote: unmounting it is how the typed text would be lost
          on a mis-click. */}
      <RuledOutNote
        stepNo={step.step_no}
        marked={marked}
        storedNote={storedNote}
        readOnly={readOnly}
        onNote={onNote}
      />
    </li>
  );
}

/**
 * Renders trail steps grouped under TURN N headings that rhyme with the
 * transcript pane, with compact segmented mark controls, optional
 * reorder/delete actions, and click-to-highlight.
 *
 * Props:
 *   steps       — array of step objects (localSteps in TrailReview)
 *   results     — { [step_no]: "worked"|"ruled_out"|"inconclusive" }
 *   notes       — { [step_no]: text } — the live, not-yet-saved "why" behind a
 *                 Ruled out mark. OPTIONAL: omitted, each card falls back to
 *                 the note the backend stored on the step.
 *   onNote      — (stepNo, value) => void, fired on blur / Enter. Omitted, the
 *                 note is still SHOWN but cannot be edited. Narration cards get
 *                 neither prop: nothing is ruled out there through the UI.
 *   readOnly    — disable marking
 *   canEdit     — show reorder/delete controls
 *   activeTurn  — turn currently selected/in view; highlights its group and
 *                 scrolls it into view when it changes
 *   onMark      — (stepNo, value) => void
 *   onStepClick — (turnNo) => void
 *   onMoveStep  — (flatIdx, direction) => void   (only if canEdit)
 *   onRemoveStep — (stepNo) => void              (only if canEdit)
 */
export function StepList({
  steps = [],
  results = {},
  notes = {},
  readOnly = false,
  canEdit = false,
  confirmedMarks = {},
  activeTurn = null,
  onMark,
  onNote,
  onStepClick,
  onMoveStep,
  onRemoveStep,
}) {
  const groups = useMemo(() => groupStepsByTurn(steps), [steps]);
  const groupRefs = useRef({});
  const prevActiveRef = useRef(null);

  /* Bring the active turn group into view when the selection changes.
     `block: "nearest"` means an already-visible group does not move, so a
     click on a visible group never yanks the pane. Nothing observes the
     left pane's scroll, so this cannot feed back into the transcript. */
  useEffect(() => {
    if (activeTurn == null) return;
    if (activeTurn === prevActiveRef.current) return;
    prevActiveRef.current = activeTurn;
    const el = groupRefs.current[activeTurn];
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeTurn]);

  if (!steps.length) {
    return <p className="tr-empty-hint">No steps captured yet.</p>;
  }

  return (
    <div className="tr-turn-groups">
      {groups.map((g) => {
        const isActive = g.turnNo != null && activeTurn != null && g.turnNo === activeTurn;
        const clickable = g.turnNo != null && !!onStepClick;
        /* Say how much of the group is still WORK, not just how long it is.
           Counting what is left to mark (rather than what is markable) is
           what makes the number mean the same thing in every group: a turn
           whose steps are all markable used to drop the "to mark" half
           entirely and read as a bare step count. */
        const markable = g.items.filter(
          ({ step }) => isMarkable(step) && !results[step.step_no]
        ).length;
        const allMarkable = g.items.every(({ step }) => isMarkable(step));
        return (
          <section
            key={g.turnNo == null ? "no-turn" : `turn-${g.turnNo}`}
            ref={(el) => {
              if (g.turnNo != null) groupRefs.current[g.turnNo] = el;
            }}
            className={`tr-turn-group${isActive ? " tr-turn-group--active" : ""}`}
          >
            <div
              className={`tr-turn-group-head${clickable ? " tr-turn-group-head--clickable" : ""}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onStepClick(g.turnNo) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onStepClick(g.turnNo);
                      }
                    }
                  : undefined
              }
            >
              <span className="tr-turn-group-label">
                {g.turnNo == null ? "Not tied to a turn" : `Turn ${g.turnNo}`}
              </span>
              <span className="tr-turn-group-line" />
              <span className="tr-turn-group-count">
                {markable > 0
                  ? `${markable} to mark · ${g.items.length} step${g.items.length > 1 ? "s" : ""}`
                  : `${allMarkable ? "all marked" : "nothing to mark"} · ${g.items.length} step${g.items.length > 1 ? "s" : ""}`}
              </span>
            </div>

            <StepItems
              items={g.items}
              total={steps.length}
              results={results}
              notes={notes}
              readOnly={readOnly}
              canEdit={canEdit}
              confirmedMarks={confirmedMarks}
              onMark={onMark}
              onNote={onNote}
              onStepClick={onStepClick}
              onMoveStep={onMoveStep}
              onRemoveStep={onRemoveStep}
            />
          </section>
        );
      })}
    </div>
  );
}

/* ── OutcomeSelector ────────────────────────────────────────────────── */

/**
 * The "How did it end?" radio group — four selectable cards.
 *
 * Native radios stay in the DOM (visually hidden, not display:none), so the
 * browser supplies role=radio / checked state, one tab stop for the group and
 * arrow-key movement between options. The whole card is the <label>, so the
 * whole card is the click target. Read-only renders no inputs (as before) and
 * shows the chosen card only as a static state.
 *
 * The section heading is the caller's (both callers render a .tr-section-label
 * inside .tr-outcome-section; trail-steps.css restyles it there).
 *
 * Props:
 *   outcome   — current selected value
 *   readOnly  — disable editing
 *   onChange  — (value) => void
 *   suggested — optional: the outcome capture INFERRED (trail.outcome when
 *               trail.outcome_source === "inferred"); that card carries an
 *               "AI suggested" tag. Omit and no tag renders.
 */
/**
 * The outcome the EXTRACTOR suggests (trail.suggested_outcome, from meta —
 * e.g. the engineer said the problem cleared with no change made), or null.
 * Never when a human already chose the outcome. Callers prefill from it and
 * keep the explicit confirm step; it never writes anything by itself.
 */
export function extractorSuggestedOutcome(trail) {
  if (!trail || trail.outcome_source === "engineer") return null;
  const value = trail.suggested_outcome?.outcome;
  return OUTCOMES.some((o) => o.value === value) ? value : null;
}

export function OutcomeSelector({ outcome, readOnly = false, onChange, suggested = null }) {
  // Unique per instance so two selectors on one page never share a group.
  const name = `outcome-${useId()}`;
  return (
    <div
      className="tr-outcome-group"
      role={readOnly ? "group" : "radiogroup"}
      aria-label="How did it end?"
    >
      <div className="tr-outcomes">
        {OUTCOMES.map((o) => {
          const active = outcome === o.value;
          const OIcon = o.Icon;
          return (
            <label
              key={o.value}
              className={`tr-outcome tr-outcome--${o.tone} ${active ? "tr-outcome--active" : ""} ${readOnly ? "tr-outcome--ro" : ""}`}
            >
              {!readOnly && (
                <input
                  type="radio"
                  name={name}
                  value={o.value}
                  className="tr-outcome-radio"
                  checked={active}
                  onChange={() => onChange?.(o.value)}
                />
              )}
              {/* focus ring: a sibling of the input so :focus-visible can reach it */}
              <span className="tr-outcome-ring" aria-hidden="true" />
              <span className="tr-outcome-top">
                <span className="tr-outcome-icon" aria-hidden="true">
                  <OIcon size={15} strokeWidth={2.2} />
                </span>
                {suggested === o.value && (
                  <span className="tr-ai-badge tr-outcome-ai" title="Inferred by AI from the session — confirm or pick another">
                    AI suggested
                  </span>
                )}
                <span className="tr-outcome-check" aria-hidden="true">
                  {active && <Check size={11} strokeWidth={3} />}
                </span>
              </span>
              <span className="tr-outcome-text">
                <b className="tr-outcome-label">{o.label}</b>
                <small className="tr-outcome-hint">{o.hint}</small>
                {o.note && <small className="tr-outcome-note">{o.note}</small>}
                {readOnly && active && <span className="tr-sr-only"> (selected)</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ── Case fields ────────────────────────────────────────────────────── */

/** The editable case fields, in the order engineers read them.
    Diagnostics is deliberately NOT here — it is derived from the steps. */
export const CASE_FIELDS = [
  {
    key: "symptom_text",
    label: "Symptom",
    rows: 2,
    placeholder: "What the customer actually experienced — not the raw first message...",
  },
  {
    key: "possible_causes",
    label: "Possible causes",
    rows: 3,
    optional: true,
    add: "Add possible causes",
    placeholder: "What the engine surfaced, and what else was considered...",
  },
  {
    key: "resolution",
    label: "Resolution",
    rows: 3,
    optional: true,
    add: "Add a resolution",
    placeholder: "The fix or the workaround that ended it...",
  },
];

/** The existing free-text takeaway, persisted as `summary`. */
export const LEARNED_FIELD = {
  key: "summary",
  label: "What we learned",
  rows: 3,
  optional: true,
  add: "Add what we learned",
  placeholder: "The insight an engineer should take away from this session...",
};

/** Every key we read from the trail and send back in a PATCH. */
export const CASE_FIELD_KEYS = [
  ...CASE_FIELDS.map((f) => f.key),
  LEARNED_FIELD.key,
];

/** Keys the backend has always accepted. Used as the degraded retry set. */
export const LEGACY_CASE_KEYS = new Set(["symptom_text", "summary"]);

export const EMPTY_CASE_FIELDS = Object.freeze(
  Object.fromEntries(CASE_FIELD_KEYS.map((k) => [k, ""]))
);

/** Coerce anything the API hands us into a safe string — never "undefined". */
export const asText = (v) => {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
};

/** Pull the case fields out of a trail object, tolerating missing columns. */
export function caseFieldsFromTrail(trail) {
  const out = { ...EMPTY_CASE_FIELDS };
  if (!trail) return out;
  for (const k of CASE_FIELD_KEYS) out[k] = asText(trail[k]);
  return out;
}

/**
 * Derive the DIAGNOSTICS lines from the trail's own steps — what was
 * actually walked, in order. Nothing here is stored or PATCHed.
 *
 * Preference order: explicit `check` steps (the checks the engineer ran);
 * if the trail has none, fall back to every non-conclusion step so the
 * roll-up is still useful. Each line carries the mark when one exists.
 */
export function deriveDiagnostics(steps = [], results = {}) {
  const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
  if (!list.length) return [];
  const checks = list.filter((s) => s.type === "traversed");
  const source = checks.length ? checks : list.filter((s) => s.type !== "resolution");
  const lines = [];
  for (const step of source) {
    const text = asText(stepHeadline(step)).trim();
    if (!text) continue;
    const mark = results[step.step_no] || step.result;
    const markLabel =
      mark && mark !== "unknown"
        ? RESULTS.find((r) => r.value === mark)?.label || mark
        : "";
    const line = markLabel ? `${text} — ${markLabel}` : text;
    // Collapse consecutive duplicates (a repeated check reads as noise).
    if (lines[lines.length - 1] === line) continue;
    lines.push(line);
  }
  return lines;
}

/**
 * Compose the read-only SESSION SUMMARY: the editable case fields plus the
 * DIAGNOSTICS block derived from the steps, in reading order.
 * Always derived — never independently editable, never persisted.
 */
export function composeSessionSummary(values = {}, steps = [], results = {}) {
  const parts = [];
  const push = (label, body) => {
    const v = asText(body).trim();
    if (v) parts.push(`${label.toUpperCase()}\n${v}`);
  };
  push("Symptom", values.symptom_text);
  push("Possible causes", values.possible_causes);
  push("Diagnostics", deriveDiagnostics(steps, results).map((l) => `- ${l}`).join("\n"));
  push("Resolution", values.resolution);
  return parts.join("\n\n");
}

const EXTENDED_MSG =
  "Symptom and notes saved. Possible causes and resolution are not accepted by the server yet — your text is kept here and will save once that ships.";

/**
 * PATCH whatever the user changed.
 *
 * `dirtyRef.current` is a { [key]: true } map; keys are cleared only on a
 * confirmed save, so a failure never loses what was typed — the next blur
 * retries it.
 *
 * If the full PATCH is rejected (the backend has not added
 * possible_causes / resolution yet) we retry with just the
 * legacy keys so the engineer's symptom and takeaway still persist, and
 * report that the rest is pending.
 *
 * Returns { status: "noop"|"saved"|"partial"|"failed", message? }.
 */
export async function saveCaseFields(patchFn, fields, dirtyRef) {
  const keys = CASE_FIELD_KEYS.filter((k) => dirtyRef.current?.[k]);
  if (!keys.length) return { status: "noop" };
  const pick = (ks) => Object.fromEntries(ks.map((k) => [k, asText(fields[k])]));
  const clear = (ks) => ks.forEach((k) => { delete dirtyRef.current[k]; });

  try {
    await patchFn(pick(keys));
    clear(keys);
    return { status: "saved" };
  } catch (e) {
    const legacy = keys.filter((k) => LEGACY_CASE_KEYS.has(k));
    if (legacy.length && legacy.length < keys.length) {
      try {
        await patchFn(pick(legacy));
        clear(legacy);
        return { status: "partial", message: EXTENDED_MSG };
      } catch {
        /* fall through and report the original failure */
      }
    }
    const msg =
      e instanceof Error && e.message
        ? e.message
        : "Could not save your edits. The text you typed is still here — try again.";
    return { status: "failed", message: msg };
  }
}

/**
 * A textarea that is exactly as tall as what is in it.
 *
 * A fixed `rows` made the EMPTY fields the biggest objects on the screen —
 * three 77px boxes reading as "extraction failed" next to a 58px box that
 * actually held 189 characters. Height now follows content: one line when
 * there is one line, as many as the text needs after that.
 *
 * `scrollHeight` is 0 under jsdom, so the write is guarded — a test render
 * must not end up with an explicit `height: 0px`.
 */
function AutoTextarea({ value, minRows = 1, className = "", ...rest }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => { resize(); }, [value, resize]);
  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      className={`tr-summary-input tr-autosize ${className}`.trim()}
      onInput={resize}
      {...rest}
    />
  );
}

/** Small copy-to-clipboard button with no external dependency. */
function CopyButton({ text, title = "Copy", label = "Copy" }) {
  const [state, setState] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((s) => {
    setState(s);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState(""), 1600);
  }, []);

  const copy = useCallback(async (e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Non-secure-context fallback (no HTTPS) — execCommand still works.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy rejected");
      }
      flash("Copied");
    } catch {
      flash("Copy failed");
    }
  }, [text, flash]);

  return (
    <button
      type="button"
      className="tr-copy-btn"
      onClick={copy}
      disabled={!text}
      title={text ? title : "Nothing to copy yet"}
    >
      <Copy size={13} />
      <span>{state || label}</span>
    </button>
  );
}

/** Every field the case record holds, in reading order. */
const ALL_CASE_FIELDS = [...CASE_FIELDS, LEARNED_FIELD];

/**
 * Has a human touched this case record yet?
 *
 * Only `symptom_text` is written by the capture pipeline, so a trail where
 * everything else is empty is a trail nobody has edited — and that is the one
 * that should open with the TRAIL in view, not with 715px of mostly-blank
 * form above it.
 */
export function caseFieldsEdited(values = EMPTY_CASE_FIELDS) {
  return ALL_CASE_FIELDS.some(
    (f) => f.key !== "symptom_text" && !!asText(values[f.key]).trim()
  );
}

/** The one line that stands in for the whole block while it is folded. */
export function caseFieldsPreview(values = EMPTY_CASE_FIELDS) {
  const empty = ALL_CASE_FIELDS.filter((f) => !asText(values[f.key]).trim());
  const head = asText(values.symptom_text).trim() ? "Symptom set" : "No symptom yet";
  if (!empty.length) return `${head} · all fields filled`;
  return `${head} · ${empty.length} field${empty.length > 1 ? "s" : ""} empty`;
}

/**
 * The case block, folded into ONE disclosure.
 *
 * What changed and why: these fields used to occupy the top 53% of the left
 * pane's scroll height on a trail nobody had edited — three empty boxes, a
 * fully duplicated roll-up, and a heading — which pushed the first step card
 * to 932px and put its own mark buttons below the fold. The trail is what the
 * screen is for, so the case record now starts collapsed unless somebody has
 * actually written in it, and the roll-up inside it is collapsed again on top
 * of that. Neither is deleted: the roll-up is a deliberate paste-into-a-ticket
 * artefact, so its Copy button stays reachable from the folded header.
 *
 * Props:
 *   values    — { symptom_text, possible_causes, resolution, summary }
 *   steps     — trail steps, used only to derive the DIAGNOSTICS block
 *   results   — { [step_no]: mark }, annotates the derived diagnostics
 *   readOnly  — render static text instead of inputs
 *   idPrefix  — makes the label/for ids unique per screen
 *   error     — message shown above the block (save failure / degraded save)
 *   errorTone — "error" (default) | "warn"
 *   defaultOpen — force the fold open/closed; omit to decide from `values`
 *   onChange  — (key, value) => void
 *   onBlur    — (key) => void
 */
export function CaseFields({
  values = EMPTY_CASE_FIELDS,
  steps = [],
  results = {},
  readOnly = false,
  idPrefix = "case",
  error = "",
  errorTone = "error",
  defaultOpen,
  onChange,
  onBlur,
}) {
  const rollup = useMemo(
    () => composeSessionSummary(values, steps, results),
    [values, steps, results]
  );

  /* `values` arrives AFTER mount (the trail is fetched), so the default has
     to stay derived until the user actually toggles — a plain useState
     initialiser would freeze the answer at "empty trail" forever. */
  const [openOverride, setOpenOverride] = useState(null);
  const autoOpen = defaultOpen != null ? defaultOpen : caseFieldsEdited(values);
  const open = openOverride == null ? autoOpen : openOverride;

  const [rollupOpen, setRollupOpen] = useState(false);
  /* An optional field the engineer asked for by clicking "+ Add …": it has no
     text yet, so nothing else would keep the box on screen. */
  const [revealed, setRevealed] = useState({});

  const preview = caseFieldsPreview(values);
  const bodyId = `${idPrefix}-case-body`;

  const renderField = (f) => {
    const id = `${idPrefix}-${f.key}`;
    const val = asText(values[f.key]);

    if (readOnly) {
      return (
        <div className="tr-section" key={f.key}>
          <span className="tr-section-label">{f.label}</span>
          <p className={`tr-summary-ro ${val ? "" : "tr-muted"}`}>
            {val || "Not filled in."}
          </p>
        </div>
      );
    }

    /* An empty optional field is an invitation, not a void. One line, not a
       77px box that reads as a failed extraction. */
    if (f.optional && !val && !revealed[f.key]) {
      return (
        <div className="tr-section tr-section--add" key={f.key}>
          <button
            type="button"
            className="tr-add-field"
            onClick={() => setRevealed((r) => ({ ...r, [f.key]: true }))}
          >
            <Plus size={12} />
            <span>{f.add || `Add ${f.label.toLowerCase()}`}</span>
          </button>
        </div>
      );
    }

    return (
      <div className="tr-section" key={f.key}>
        <label className="tr-section-label" htmlFor={id}>{f.label}</label>
        <AutoTextarea
          id={id}
          value={val}
          minRows={1}
          autoFocus={!!revealed[f.key]}
          placeholder={f.placeholder}
          onChange={(e) => onChange?.(f.key, e.target.value)}
          onBlur={() => onBlur?.(f.key)}
        />
      </div>
    );
  };

  return (
    <div className="tr-case">
      {error && (
        <p className={errorTone === "warn" ? "tr-warn-text" : "tr-error-text"}>
          {error}
        </p>
      )}

      <div className="tr-case-head">
        <button
          type="button"
          className="tr-disclose tr-case-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpenOverride(!open)}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <span className="tr-case-title">Conversation record</span>
          <span className="tr-case-preview">{preview}</span>
        </button>
        {/* The roll-up's only job is being pasted into a ticket, so Copy has
            to work without unfolding anything. */}
        <CopyButton
          text={rollup}
          label="Copy summary"
          title="Copy the session summary"
        />
      </div>

      {open && (
        <div className="tr-case-body" id={bodyId}>
          {ALL_CASE_FIELDS.map(renderField)}

          {/* Derived roll-up — never independently editable, and byte-for-byte
              a restatement of what is already on this screen, so it is folded
              by default and styled as a document rather than an input. */}
          <div className="tr-section tr-rollup-section">
            <div className="tr-rollup-head">
              <button
                type="button"
                className="tr-disclose"
                aria-expanded={rollupOpen}
                onClick={() => setRollupOpen((o) => !o)}
              >
                {rollupOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className="tr-section-label tr-section-label--flush">
                  Session summary
                </span>
                <span className="tr-rollup-hint">the same text, as one block</span>
              </button>
            </div>
            {rollupOpen && (
              <div className={`tr-rollup ${rollup ? "" : "tr-muted"}`}>
                {rollup || "Fill in the fields above and the summary composes itself."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StepList;
